import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Step {
  label: string;
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  className?: string;
  /**
   * Quando definido, cada passo já completado vira um botão clicável
   * que dispara o callback. Útil para fluxos onde o usuário pode revisar
   * uma etapa anterior sem perder o estado já preenchido.
   */
  onStepClick?: (step: number) => void;
}

/**
 * Renderiza o padrão `.ds-progress-steps` do design system: bolinhas
 * inline com rótulo ao lado, conectadas por linha horizontal. Estilo
 * vivo na rota /admin/design-system, seção "Barras de progresso".
 */
export function StepIndicator({
  steps,
  currentStep,
  className,
  onStepClick,
}: StepIndicatorProps) {
  return (
    <div className={cn('ds-progress-steps', className)}>
      {steps.map((step, i) => {
        const stepNum = i + 1;
        const isCompleted = stepNum < currentStep;
        const isActive = stepNum === currentStep;
        const isClickable = !!onStepClick && stepNum < currentStep;

        const itemClasses = cn(
          'ds-progress-step',
          isCompleted && 'is-done',
          isActive && 'is-current',
          isClickable && 'ds-progress-step--clickable',
        );

        const itemContent = (
          <>
            <span className="ds-progress-step__dot">
              {isCompleted ? (
                <Check size={12} strokeWidth={3} aria-hidden="true" />
              ) : (
                stepNum
              )}
            </span>
            {step.label}
          </>
        );

        return (
          <React.Fragment key={i}>
            {isClickable ? (
              <button
                type="button"
                onClick={() => onStepClick?.(stepNum)}
                aria-label={`Voltar para ${step.label}`}
                className={itemClasses}
              >
                {itemContent}
              </button>
            ) : (
              <span
                className={itemClasses}
                aria-current={isActive ? 'step' : undefined}
              >
                {itemContent}
              </span>
            )}
            {i < steps.length - 1 ? (
              <span
                className={cn(
                  'ds-progress-step__line',
                  isCompleted && 'is-done',
                )}
                aria-hidden="true"
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}
